/**
 * @module Return Visitor Modal
 * @description Return Visitor Modal
 */

import { useRouter } from "next/router";
import { useEffect, useContext, useState } from "react";
import MicroModal from "micromodal";
import PropTypes from "prop-types";
import Link from "next/link";
import { setCookie, getCookie } from "@/js/utils/cookie";
import { stringify } from "@/js/utils/filters";
import { FuelContext } from "@/providers/fuel-provider";
import { trackEvent } from "@/js/helpers/tracking";
import Tooltip from "@/blocks/Tooltip";

import styles from "./index.module.css";
import { formatPhoneNumber } from "@/js/helpers/fuse";
import { DEFAULT_FUSE_NUMBER } from "@/js/constants/monarch";

const getDefaultBlocks = (phoneNumber) => [
  {
    title: "Shop",
    content: (
      <>
        Order now and get free installation & a premium Wi-Fi router included
        with all fiber plans.
        <Tooltip disclosure="includes-tech-installation" />
      </>
    ),
    link: {
      url: null,
      title: "Shop Plans",
      payload: {
        text: "Shop Plans",
        actionOutcome: "INTERNALLINK",
      },
    },
  },
  {
    title: "Learn",
    content:
      "Upgrade to Frontier Fiber and we'll include a Wi-Fi router at no additional cost.",
    link: {
      url: "/shop",
      title: "Learn more",
      payload: {
        text: "Learn more",
        actionOutcome: "INTERNALLINK",
      },
    },
  },
  {
    title: "Call",
    content: "Our professionals will find the right internet plan for you.",
    link: {
      url: "tel:#",
      title: formatPhoneNumber(phoneNumber),
      payload: {
        text: `Phone Number ${formatPhoneNumber(phoneNumber)}`,
        actionOutcome: "CLICKTOCALL",
      },
    },
    fuse: true,
  },
];

/**
 * @function Component
 * @description
 */
const Component = ({ ...props }) => {
  // TODO: Let’s wait for instructions here. Based on some chats, the QA document, and the diagram, this variable should be set to true on the Cart-FE side.
  // const { fuseNumber, returnVisitorOfferCookieEnabled } = useContext( FuelContext );
  const { fuseNumber } = useContext(FuelContext);
  const {
    title = "We're glad you're back!",
    subtitle = (
      <>
        <span className="u-color-red">New Offer:</span> Save $10 on your monthly
        bill when you sign up for Auto Pay, with installation as early as
        tomorrow.
      </>
    ),
    blocks = getDefaultBlocks(fuseNumber || DEFAULT_FUSE_NUMBER),
  } = props;
  const router = useRouter();
  const [modalVisible, setModalVisible] = useState(false);

  const { clickThrough } = useContext(FuelContext);

  /**
   * @function handleClick
   * @description fire off tagular events
   */
  const handleClick = (event) => {
    const { target } = event;
    const payload = JSON.parse(target.getAttribute("data-payload"));
    const { text, actionOutcome } = payload;

    const data = {
      elementType: "BUTTON",
      position: "RETURN VISITOR MODAL",
      text,
      actionOutcome,
    };

    trackEvent({ action: "elementClicked", data, event });
  };

  /**
   * @function handleClose
   * @description handle modal close
   */
  const handleClose = () => {
    const data = {
      elementType: "BUTTON",
      position: "RETURN VISITOR MODAL",
      text: "Close",
      actionOutcome: "INTERNALLINK",
    };

    trackEvent({ action: "elementClicked", data, event });

    setModalVisible(false);
  };

  /**
   * @function useEffect
   * @description initialize the modal
   */
  useEffect(() => {
    if (modalVisible === false) {
      return;
    }

    MicroModal.init({
      openClass: "is-open",
      disableScroll: true,
      awaitOpenAnimation: false,
      awaitCloseAnimation: false,
      debugMode: false,
    });
    MicroModal.show("modal-return-visitor", {
      onClose: handleClose,
      disableFocus: true,
    });

    const data = {
      elementType: "MODAL",
      position: "RETURN VISITOR MODAL",
    };

    trackEvent({ action: "elementViewed", data, event });
  }, [modalVisible]);

  /**
   * @function useEffect
   * @description Query param to trigger manually
   */
  useEffect(() => {
    if ("showModal" in router.query) {
      setModalVisible(true);
    }
  }, [router.query]);

  /**
   * @function useEffect
   * @description Kick off this modules functions
   */
  useEffect(() => {
    // Read cookies first in one batch
    const hasSeenModal = getCookie("hasSeenModal");
    const sessionCount = getCookie("tglr_sess_count");
    const currentSessionId = getCookie("tglr_sess_id");
    const lastSessionId = getCookie("last_sess_id");

    // SESSION CHANGE CHECK:
    // If we have both current and last session IDs, check if they match
    if (
      currentSessionId &&
      lastSessionId &&
      lastSessionId !== "undefined" &&
      lastSessionId !== "null"
    ) {
      // If there's a mismatch between session IDs
      if (currentSessionId !== lastSessionId) {
        // Update the stored session ID
        setCookie("last_sess_id", currentSessionId, 10080, {
          otGroup: "C0002",
        }); // 7 days
        // Reset hasSeenModal to 0 so modal will show on next page load
        setCookie("hasSeenModal", "0", 10080, { otGroup: "C0002" });
        // Show the modal immediately since we know this is a return visitor
        setModalVisible(true);
        return;
      }
    } else if (currentSessionId) {
      // If we have current ID but no last ID, store it for future comparison
      setCookie("last_sess_id", currentSessionId, 10080, { otGroup: "C0002" }); // 7 days
    }

    // If user has already seen the modal or it's a new session (tglr_sess_count === '1'), bail immediately
    if (hasSeenModal === "1" || sessionCount === "1") {
      return;
    }

    // FIRST-TIME VISITOR CHECK:
    // If this is a brand new visitor (first time ever on the site)
    // We'll set hasSeenModal to 0 but NOT show the modal
    if (hasSeenModal === undefined || hasSeenModal === "undefined") {
      setCookie("hasSeenModal", "0", 10080, { otGroup: "C0002" });
      // Don't show modal to first-time visitors, just set the cookie for next time
      return;
    }

    // MODAL DISPLAY LOGIC:
    // Now handle normal modal display logic based on hasSeenModal
    if (hasSeenModal === "0") {
      setModalVisible(true);
      setCookie("hasSeenModal", "1", 10080, { otGroup: "C0002" }); // Mark as seen for 7 days

      // Set session flag for Cart to prevent double modal in same session
      // Cart will check this flag and skip showing RVO modal if user already saw it on LP
      if (typeof window !== "undefined" && window.sessionStorage) {
        window.sessionStorage.setItem("rvoShownOnLandingPage", "true");
      }
    }
  }, []);

  return (
    <>
      {modalVisible && (
        <section className={`component ${styles.component}`}>
          <div
            className="modal micromodal-slide"
            id="modal-return-visitor"
            aria-hidden="true"
          >
            <div className="modal__overlay" tabIndex="-1" data-micromodal-close>
              <div
                className={`modal__container ${styles.component__modalContainer}`}
                role="dialog"
                aria-modal="true"
                aria-labelledby="modal-return-visitor-title"
              >
                <button
                  className={`modal__close ${styles.component__close}`}
                  aria-label="Close modal"
                  data-micromodal-close
                ></button>
                <div className={styles.component__contentWrapper}>
                  <div className={`modal__header ${styles.component__header}`}>
                    <p
                      className={`h4 ${styles.component__title}`}
                      id="modal-return-visitor-title"
                    >
                      {title}
                    </p>
                    <p className={styles.component__subtitle}>{subtitle}</p>
                  </div>
                  {/* <!-- .modal__header --> */}

                  <div
                    className={`modal__content ${styles.component__body}`}
                    id="modal-return-visitor-content"
                  >
                    <div className={styles.component__blocks}>
                      {blocks.map((block, index) => (
                        <div key={index} className={styles.component__block}>
                          <h3 className={`h5 ${styles.component__blockTitle}`}>
                            {block.title}
                          </h3>
                          <div
                            className={`lead ${styles.component__blockContent}`}
                          >
                            {block.content}
                          </div>
                          <Link
                            href={block.link.url || clickThrough}
                            className={`btn btn--red btn-small ${styles.component__blockBtn}`}
                            data-payload={stringify(block.link.payload)}
                            onClick={handleClick}
                          >
                            {block.link.title}
                          </Link>
                        </div>
                      ))}{" "}
                      {/* <!-- .modal__block --> */}
                    </div>
                    {/* <!-- .component__block --> */}
                  </div>
                  {/* <!-- .modal__content --> */}
                </div>
                {/* <!-- .component__contentWrapper --> */}
              </div>
              {/* <!-- .modal__container --> */}
            </div>
            {/* <!-- .modal__overlay --> */}
          </div>
          {/* <!-- .modal --> */}
        </section>
      )}
    </>
  );
};

Component.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.node,
  blocks: PropTypes.arrayOf(
    PropTypes.shape({
      icon: PropTypes.string,
      title: PropTypes.string,
      content: PropTypes.node,
      link: PropTypes.shape({
        url: PropTypes.string,
        title: PropTypes.string,
        payload: PropTypes.shape({
          text: PropTypes.string,
          actionOutcome: PropTypes.string,
        }),
      }),
      fuse: PropTypes.bool,
    }),
  ),
};

export default Component;
